// TODO: evaluate `use-query-params` for this hook in a separate ticket. It owns the URL read/write
// and typed encoders; the wire translation in httpQueryTranslation stays either way. Needs a
// QueryParamProvider at the app root with the react-router-5 adapter, so it is not a WAK-40065 change.
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import type {
  UrlPageQueryState,
  PaginatedQueryTranslation,
} from "../utils/httpQueryTranslation";

export interface UsePageQueryUrlStateResult<State extends UrlPageQueryState> {
  urlState: State;
  updateUrlState: (changes: Partial<State>) => void;
}

const usePageQueryUrlState = <State extends UrlPageQueryState>(
  translation: PaginatedQueryTranslation<State>,
  filterKeys: readonly (keyof State)[]
): UsePageQueryUrlStateResult<State> => {
  const history = useHistory();
  const { search } = useLocation();

  const urlState = useMemo(() => translation.parse(search), [translation, search]);

  const updateUrlState = useCallback(
    (changes: Partial<State>) => {
      const current = translation.parse(history.location.search);
      const filtersChanged = filterKeys.some(
        (key) => changes[key] !== undefined
      );
      const next: State = {
        ...current,
        ...changes,
        ...(filtersChanged && changes.page === undefined ? { page: 1 } : {}),
      };
      const nextSearch = translation.build(next);
      if (nextSearch === translation.build(current)) {
        return;
      }
      history.replace({
        pathname: history.location.pathname,
        search: nextSearch,
        state: history.location.state,
      });
    },
    [translation, filterKeys, history]
  );

  return { urlState, updateUrlState };
};

export default usePageQueryUrlState;
